import { NoddiDateFormats, noddiFormatDate, toMinor } from 'noddi-util';
import type { DateRange } from 'react-day-picker';
import { decodeDate } from 'use-query-params';
import { parseNumber, type Stored, storedParam, storedValue, toStored, toUrlKey } from './filterSelection';
import { type ControlText, excludedKeyOf, type FilterSpec, type UrlValues } from './filterSpecs';
import { brand } from './filterUnitType';

const toApiDate = (date: Date) => noddiFormatDate(date, NoddiDateFormats.API_DATE);

// The URL holds the date the API gets, read back as a local day.
const DATE_PARAM = storedParam<Date>(toApiDate, (value) => decodeDate(value) ?? undefined);
const NUMBER_PARAM = storedParam<number>(String, parseNumber);
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const TIME_PARAM = storedParam<string>(String, (value) => (TIME_PATTERN.test(value) ? value : undefined));

/** Static defaults, or a function for ranges relative to today (computed when the list renders). */
export type DateRangeDefaults = DateRange | (() => DateRange);

type Bounds<T> = { from?: T; to?: T };
type BoundKeys = { from: string; to: string };

const isEmpty = (bounds: Bounds<unknown> | undefined) => bounds?.from == null && bounds?.to == null;

const sameBounds = (a: Bounds<Date> | undefined, b: Bounds<Date> | undefined) =>
  a?.from?.getTime() === b?.from?.getTime() && a?.to?.getTime() === b?.to?.getTime();

/**
 * Two URL keys read as one value with the three states of a selection: both absent = not chosen (defaults),
 * otherwise each bound as stored.
 */
const boundsState = <T>(values: UrlValues, urlKeys: BoundKeys, defaults: Bounds<T> | undefined): Bounds<T> => {
  const from = values[urlKeys.from] as Stored<T>;
  const to = values[urlKeys.to] as Stored<T>;
  return from === undefined && to === undefined
    ? (defaults ?? {})
    : { from: storedValue(from, undefined), to: storedValue(to, undefined) };
};

// With a default, an unpicked bound is stored as cleared, so the default does not fill it back in.
const toStoredBounds = <T>(bounds: Bounds<T> | undefined, urlKeys: BoundKeys, hasDefaults: boolean) => ({
  [urlKeys.from]: toStored(bounds?.from, hasDefaults),
  [urlKeys.to]: toStored(bounds?.to, hasDefaults)
});

export const dateRangeSpec = ({
  from,
  to,
  exclude,
  defaults,
  text,
  placeholder
}: {
  from: string;
  to: string;
  exclude: boolean;
  defaults: DateRangeDefaults | undefined;
  text?: ControlText;
  placeholder?: string;
}): FilterSpec<object> => {
  const keys = { from, to };
  const excludedKeys = { from: excludedKeyOf(from), to: excludedKeyOf(to) };
  const urlKeys = { from: toUrlKey(from), to: toUrlKey(to) };
  const excludedUrlKeys = { from: toUrlKey(excludedKeys.from), to: toUrlKey(excludedKeys.to) };
  const resolveDefaults = () => (typeof defaults === 'function' ? defaults() : defaults);
  const hasDefaults = !isEmpty(resolveDefaults());

  return {
    kind: 'filter-spec',
    signature: `${from}:${to}:${exclude}:date`,
    // The toolbar range renders no sidebar control.
    controlIds: text ? [from] : [],
    urlParams: {
      [urlKeys.from]: DATE_PARAM,
      [urlKeys.to]: DATE_PARAM,
      ...(exclude && { [excludedUrlKeys.from]: DATE_PARAM, [excludedUrlKeys.to]: DATE_PARAM })
    },
    resolve: (values, setValues) => {
      const range = boundsState<Date>(values, urlKeys, resolveDefaults());
      const excluded = exclude ? boundsState<Date>(values, excludedUrlKeys, undefined) : {};
      const selectedValue = isEmpty(range) ? undefined : { from: range.from, to: range.to };
      const excludedValue = isEmpty(excluded) ? undefined : { from: excluded.from, to: excluded.to };
      const includeUntouched = values[urlKeys.from] === undefined && values[urlKeys.to] === undefined;
      // An include the user left alone keeps following the default instead of freezing it into the URL.
      const includeWrite = (picked: DateRange | undefined) =>
        includeUntouched && sameBounds(picked, selectedValue) ? {} : toStoredBounds(picked, urlKeys, hasDefaults);
      const onChange = (picked: DateRange | undefined) => setValues(() => includeWrite(picked));
      const reset = () =>
        setValues(() => ({
          ...toStoredBounds(undefined, urlKeys, false),
          ...(exclude && toStoredBounds(undefined, excludedUrlKeys, false))
        }));
      const apiInput = {
        [keys.from]: range.from && toApiDate(range.from),
        [keys.to]: range.to && toApiDate(range.to),
        ...(exclude && {
          [excludedKeys.from]: excluded.from && toApiDate(excluded.from),
          [excludedKeys.to]: excluded.to && toApiDate(excluded.to)
        })
      };

      if (!text) {
        return brand({ controls: [], dateRange: { value: selectedValue, onChange, placeholder }, apiInput });
      }
      return brand({
        controls: [
          {
            ...text,
            id: from,
            type: 'date-range',
            canExclude: exclude,
            value: { include: selectedValue, exclude: excludedValue },
            onChange: (next) =>
              setValues(() => ({
                ...includeWrite(next.include),
                ...(exclude && toStoredBounds(next.exclude, excludedUrlKeys, false))
              })),
            reset,
            isInitial: [...Object.values(urlKeys), ...(exclude ? Object.values(excludedUrlKeys) : [])].every(
              (urlKey) => values[urlKey] === undefined
            )
          }
        ],
        apiInput
      });
    }
  };
};

/**
 * `money`: the user enters kroner (URL holds them too); the endpoint takes minor units (øre), so 2 000 is sent as
 * 200 000. Without it a price field filters at a hundredth of what was typed.
 */
export const numberRangeSpec = ({
  base,
  exclude,
  money,
  text
}: {
  base: string;
  exclude: boolean;
  money: boolean;
  text: ControlText & { minLabel?: string; maxLabel?: string; minPlaceholder?: string; maxPlaceholder?: string };
}): FilterSpec<object> => {
  const toApi = (value: number | undefined) => (value === undefined ? undefined : money ? toMinor(value) : value);
  const keys = { from: `${base}Gte`, to: `${base}Lte` };
  const excludedKeys = { from: excludedKeyOf(keys.from), to: excludedKeyOf(keys.to) };
  const urlKeys = { from: toUrlKey(keys.from), to: toUrlKey(keys.to) };
  const excludedUrlKeys = { from: toUrlKey(excludedKeys.from), to: toUrlKey(excludedKeys.to) };
  const toAction = (bounds: Bounds<number>) => (isEmpty(bounds) ? undefined : { min: bounds.from, max: bounds.to });
  const fromAction = (value: { min?: number; max?: number } | undefined) => ({ from: value?.min, to: value?.max });

  return {
    kind: 'filter-spec',
    signature: `${base}:${exclude}:${money}:number`,
    controlIds: [base],
    urlParams: {
      [urlKeys.from]: NUMBER_PARAM,
      [urlKeys.to]: NUMBER_PARAM,
      ...(exclude && { [excludedUrlKeys.from]: NUMBER_PARAM, [excludedUrlKeys.to]: NUMBER_PARAM })
    },
    resolve: (values, setValues) => {
      const range = boundsState<number>(values, urlKeys, undefined);
      const excluded = exclude ? boundsState<number>(values, excludedUrlKeys, undefined) : {};
      return brand({
        controls: [
          {
            ...text,
            id: base,
            type: 'number-range',
            canExclude: exclude,
            value: { include: toAction(range), exclude: toAction(excluded) },
            onChange: (next) =>
              setValues(() => ({
                ...toStoredBounds(fromAction(next.include), urlKeys, false),
                ...(exclude && toStoredBounds(fromAction(next.exclude), excludedUrlKeys, false))
              })),
            reset: () =>
              setValues(() => ({
                ...toStoredBounds(undefined, urlKeys, false),
                ...(exclude && toStoredBounds(undefined, excludedUrlKeys, false))
              })),
            isInitial: [...Object.values(urlKeys), ...(exclude ? Object.values(excludedUrlKeys) : [])].every(
              (urlKey) => values[urlKey] === undefined
            )
          }
        ],
        apiInput: {
          [keys.from]: toApi(range.from),
          [keys.to]: toApi(range.to),
          ...(exclude && { [excludedKeys.from]: toApi(excluded.from), [excludedKeys.to]: toApi(excluded.to) })
        }
      });
    }
  };
};

/** One date into one param (`deregisteredAt`, `createdAt` on a list that filters a day). */
export const dateSpec = ({ key, text }: { key: string; text: ControlText }): FilterSpec<object> => {
  const urlKey = toUrlKey(key);
  return {
    kind: 'filter-spec',
    signature: `${key}:date`,
    controlIds: [key],
    urlParams: { [urlKey]: DATE_PARAM },
    resolve: (values, setValues) => {
      const value = storedValue(values[urlKey] as Stored<Date>, undefined);
      return brand({
        controls: [
          {
            ...text,
            id: key,
            type: 'date',
            value,
            onChange: (picked) => setValues(() => ({ [urlKey]: picked })),
            reset: () => setValues(() => ({ [urlKey]: undefined })),
            isInitial: values[urlKey] === undefined
          }
        ],
        apiInput: { [key]: value && toApiDate(value) }
      });
    }
  };
};

/** Time of day over `<base>Gte` / `<base>Lte` as `HH:MM`. A value in another shape is ignored, not sent. */
export const timeRangeSpec = ({ base, text }: { base: string; text: ControlText }): FilterSpec<object> => {
  const keys = { from: `${base}Gte`, to: `${base}Lte` };
  const urlKeys = { from: toUrlKey(keys.from), to: toUrlKey(keys.to) };
  return {
    kind: 'filter-spec',
    signature: `${base}:time`,
    controlIds: [base],
    urlParams: { [urlKeys.from]: TIME_PARAM, [urlKeys.to]: TIME_PARAM },
    resolve: (values, setValues) => {
      const range = boundsState<string>(values, urlKeys, undefined);
      return brand({
        controls: [
          {
            ...text,
            id: base,
            type: 'time-range',
            value: isEmpty(range) ? undefined : { from: range.from, to: range.to },
            onChange: (picked) => setValues(() => toStoredBounds(picked, urlKeys, false)),
            reset: () => setValues(() => toStoredBounds(undefined, urlKeys, false)),
            isInitial: values[urlKeys.from] === undefined && values[urlKeys.to] === undefined
          }
        ],
        apiInput: { [keys.from]: range.from, [keys.to]: range.to }
      });
    }
  };
};
